'use memo';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { FormItem } from '@/components/FormItem';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import NavigationCard, {
  type NavigationSectionGroup,
} from '@/components/Navigation/NavigationCard';
import {
  ScheduleDisplay,
  SchedulePicker,
  translateScheduleErrors,
  validateSchedule,
  type ScheduleFormValues,
} from '@/components/SchedulePicker';
import { SelectField } from '@/components/Select/SelectField';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { orpc } from '@/lib/api/orpc';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';

import {
  SettingsItemCard,
  SettingsItemDescription,
  SettingsItemLeftColumn,
  SettingsItemRightColumn,
  SettingsItemRow,
} from '../../../../../-components/SettingsItem';

export const Route = createFileRoute(
  '/_staff/settings/data-connector/outbound/data-pipeline/$id/_detail/execution-plan/',
)({
  component: OutboundExecutionPlanPage,
});

type OperationMode = 'RUNNING' | 'PAUSED';
type TriggerMode = 'MANUAL' | 'SCHEDULE';
type IngestionMode = 'FULL';

const DEFAULT_SCHEDULE_VALUES: ScheduleFormValues = {
  frequency: 'DAILY',
  time: '09:00',
  daysOfWeek: [],
  daysOfMonth: [],
};

/**
 * OUT 実行計画。IN（inbound）の ExecutionPlanSection と同型：
 * operationMode / triggerMode / ingestionMode + enum スケジュール（SchedulePicker）を一括更新する。
 * 文言は IN と共通の i18n（dataPipeline.detail.executionPlanSettings / scheduleSettings）を使う。
 */
function OutboundExecutionPlanPage() {
  const { id: pipelineId } = Route.useParams();
  const queryClient = useQueryClient();
  const { t: tCommon, i18n } = useTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.dataPipeline.detail.executionPlanSettings',
  });
  const { t: tSchedule } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.dataPipeline.detail.scheduleSettings',
  });
  const { t: tOut } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.outboundDataPipeline',
  });
  const { toast } = useToast();

  // モード選択肢のラベルはローカライズ（読み取り表示はサーバの *Label を使う）。
  const operationModeOptions = useMemo(
    () => [
      { id: 'RUNNING', label: tOut('operationMode.RUNNING') },
      { id: 'PAUSED', label: tOut('operationMode.PAUSED') },
    ],
    [tOut],
  );
  const triggerModeOptions = useMemo(
    () => [
      { id: 'MANUAL', label: tOut('triggerMode.MANUAL') },
      { id: 'SCHEDULE', label: tOut('triggerMode.SCHEDULE') },
    ],
    [tOut],
  );
  const ingestionModeOptions = useMemo(
    () => [{ id: 'FULL', label: tOut('ingestionMode.FULL') }],
    [tOut],
  );

  const detailQuery = useQuery({
    ...orpc.outboundDataPipeline.get.queryOptions({
      input: { id: pipelineId },
    }),
    ...QUERY_PRESETS.detail(),
  });

  const [isEditing, setIsEditing] = useState(false);
  const [operationMode, setOperationMode] = useState<OperationMode>('RUNNING');
  const [triggerMode, setTriggerMode] = useState<TriggerMode>('MANUAL');
  const [ingestionMode, setIngestionMode] = useState<IngestionMode>('FULL');
  const [scheduleValues, setScheduleValues] = useState<ScheduleFormValues>(
    DEFAULT_SCHEDULE_VALUES,
  );

  const plan = detailQuery.data?.executionPlan;

  // 編集開始時にサーバ値で編集 state をシードする（useEffect での同期 setState は避ける）。
  const handleStartEditing = () => {
    if (plan == null) return;
    setOperationMode(plan.operationMode);
    setTriggerMode(plan.triggerMode);
    setIngestionMode(plan.ingestionMode);
    setScheduleValues(
      plan.schedule
        ? {
            frequency: plan.schedule.frequency,
            time: plan.schedule.time as ScheduleFormValues['time'],
            daysOfWeek: plan.schedule.daysOfWeek,
            daysOfMonth: plan.schedule.daysOfMonth,
          }
        : DEFAULT_SCHEDULE_VALUES,
    );
    setIsEditing(true);
  };

  const updateMutation = useMutation(
    orpc.outboundDataPipeline.updateExecutionPlan.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.outboundDataPipeline.get.key({
            input: { id: pipelineId },
          }),
        });
        setIsEditing(false);
      },
      onError: (error) => {
        const { message } = handleApiError(error, {
          language: i18n.language,
        });
        toast({ description: message, variant: 'destructive' });
      },
    }),
  );

  const handleSave = () => {
    if (triggerMode === 'SCHEDULE') {
      const result = validateSchedule(scheduleValues);
      if (!result.isValid) {
        const translated = translateScheduleErrors(result.errors, (key) =>
          tCommon(key),
        );
        const firstError = Object.values(translated)[0];
        if (firstError) {
          toast({ description: firstError, variant: 'destructive' });
        }
        return;
      }
    }

    updateMutation.mutate({
      id: pipelineId,
      operationMode,
      triggerMode,
      ingestionMode,
      ...(triggerMode === 'SCHEDULE'
        ? {
            schedule: {
              frequency: scheduleValues.frequency,
              time: scheduleValues.time,
              daysOfWeek: scheduleValues.daysOfWeek,
              daysOfMonth: scheduleValues.daysOfMonth,
            },
          }
        : {}),
    });
  };

  const handleCancel = () => setIsEditing(false);

  const navigationSections: NavigationSectionGroup[] = [
    {
      titleLabel: t('title'),
      to: '/settings/data-connector/outbound/data-pipeline/$id/execution-plan',
      hash: 'execution-plan',
    },
  ];

  return (
    <FillScreen>
      <HStack className="gap-0 px-12 py-6">
        <div className="z-10 sticky top-6 w-52 shrink-0">
          <NavigationCard sections={navigationSections} />
        </div>

        <VStack className="relative z-0 min-w-0 flex-1 gap-6">
          <SettingsItemRow id="execution-plan">
            <SettingsItemLeftColumn>
              <Text variant="h3">{t('title')}</Text>
              <SettingsItemDescription>
                {t('description')}
              </SettingsItemDescription>
            </SettingsItemLeftColumn>

            <SettingsItemRightColumn>
              <SettingsItemCard className="relative overflow-visible">
                {plan == null ? (
                  <Text variant="p" className="text-neutral-muted-on-fill">
                    {tOut('loading')}
                  </Text>
                ) : isEditing ? (
                  <VStack className="gap-5">
                    <FormItem
                      fieldTitle={t('ingestionMode')}
                      fieldInput={
                        <SelectField
                          value={ingestionMode}
                          placeholder=""
                          options={ingestionModeOptions}
                          className="w-80"
                          onValueChange={(v) =>
                            setIngestionMode(v as IngestionMode)
                          }
                        />
                      }
                    />
                    <FormItem
                      fieldTitle={t('operationMode')}
                      fieldInput={
                        <SelectField
                          value={operationMode}
                          placeholder=""
                          options={operationModeOptions}
                          className="w-80"
                          onValueChange={(v) =>
                            setOperationMode(v as OperationMode)
                          }
                        />
                      }
                    />
                    <FormItem
                      fieldTitle={t('triggerMode')}
                      fieldInput={
                        <SelectField
                          value={triggerMode}
                          placeholder=""
                          options={triggerModeOptions}
                          className="w-80"
                          onValueChange={(v) =>
                            setTriggerMode(v as TriggerMode)
                          }
                        />
                      }
                    />

                    {triggerMode === 'SCHEDULE' && (
                      <VStack className="gap-3">
                        <Text
                          variant="labelSmall"
                          className="py-1 text-neutral-muted-on-fill"
                        >
                          {tSchedule('title')}
                        </Text>
                        <SchedulePicker
                          value={scheduleValues}
                          onChange={setScheduleValues}
                          hasSchedule={plan.schedule !== null}
                          isSaving={false}
                          hideSubmitButton
                        />
                      </VStack>
                    )}

                    <HStack className="justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={updateMutation.isPending}
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        onClick={handleSave}
                        disabled={updateMutation.isPending}
                      >
                        {t('save')}
                      </Button>
                    </HStack>
                  </VStack>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      className="absolute top-6 right-6 z-10"
                      onClick={handleStartEditing}
                    >
                      {t('edit')}
                    </Button>
                    <VStack className="gap-5">
                      <ReadField
                        label={t('ingestionMode')}
                        value={plan.ingestionModeLabel}
                      />
                      <ReadField
                        label={t('operationMode')}
                        value={plan.operationModeLabel}
                      />
                      <ReadField
                        label={t('triggerMode')}
                        value={plan.triggerModeLabel}
                      />

                      {plan.triggerMode === 'SCHEDULE' && plan.schedule && (
                        <VStack className="gap-3">
                          <Text
                            variant="labelSmall"
                            className="text-neutral-muted-on-fill"
                          >
                            {tSchedule('title')}
                          </Text>
                          <ScheduleDisplay
                            frequency={plan.schedule.frequency}
                            time={plan.schedule.time}
                            daysOfWeek={plan.schedule.daysOfWeek}
                            daysOfMonth={plan.schedule.daysOfMonth}
                          />
                        </VStack>
                      )}
                    </VStack>
                  </>
                )}
              </SettingsItemCard>
            </SettingsItemRightColumn>
          </SettingsItemRow>
        </VStack>
      </HStack>
    </FillScreen>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <VStack className="gap-1">
      <Text variant="labelSmall" className="text-neutral-muted-on-fill">
        {label}
      </Text>
      <Text variant="p">{value}</Text>
    </VStack>
  );
}
